<template>
  <div class="liven-engage-container">
    <div class="logs-container">
      <div class="logs-header">
        <div class="title-section">
          <h1 class="logs-title">Liven Engage Logs</h1>
          <p class="logs-subtitle">Monitor customer data sync operations</p>
        </div>
        <div class="header-actions">
          <button class="refresh-btn" @click="refreshLogs" :disabled="isLoading">
            <span v-if="isLoading">Loading...</span>
            <span v-else>Refresh</span>
          </button>
        </div>
      </div>

      <div class="filters-section">
        <div class="filter-row">
          <!-- Date Range Filters -->
          <div class="filter-item">
            <label for="start-date">Start Date</label>
            <input 
              id="start-date" 
              type="datetime-local" 
              v-model="filters.startDatetime"
              @change="onFiltersChange"
            />
          </div>
          <div class="filter-item">
            <label for="end-date">End Date</label>
            <input 
              id="end-date" 
              type="datetime-local" 
              v-model="filters.endDatetime"
              @change="onFiltersChange"
            />
          </div>

          <!-- Search -->
          <div class="filter-item">
            <label for="search">Search</label>
            <div class="search-input-group">
              <input 
                id="search" 
                type="text" 
                v-model="filters.searchTerm" 
                placeholder="Search in message, log level, company id (e.g. company_id:123)..."
              />
              <button @click="onFiltersChange">Search</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Display error message if any -->
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <!-- Logs Table -->
      <div class="logs-table-container">
        <table class="logs-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Message</th>
              <th>Log Level</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="logs.length === 0">
              <tr>
                <td colspan="4" class="no-data">No logs found</td>
              </tr>
            </template>
            <template v-else>
              <template v-for="(log, index) in logs" :key="log.id">
                <tr :class="{ 'error-row': log.log_level === 'error' }">
                  <td>{{ formatDate(log.timestamp) }}</td>
                  <td class="message-cell">{{ getLogMessage(log) }}</td>
                  <td>
                    <span :class="`log-level-badge ${log.log_level}`">
                      {{ log.log_level || '-' }}
                    </span>
                  </td>
                  <td>
                    <button 
                      class="expand-btn"
                      @click="toggleDetails(index)"
                    >
                      {{ expandedRows[index] ? 'Hide' : 'Show' }} Details
                    </button>
                  </td>
                </tr>
                <tr v-if="expandedRows[index]" class="details-row">
                  <td colspan="4">
                    <div class="details-content">
                      <h4>Additional Details</h4>
                      <div class="full-message">
                        <h5>Message</h5>
                        <pre>{{ log.message || '-' }}</pre>
                      </div>
                      <div v-if="log.extra" class="json-viewer">
                        <h5>Extra Data</h5>
                        <vue-json-pretty :data="parseJsonData(log.extra)" />
                      </div>
                      <pre v-else>{{ log.details || 'No additional details available' }}</pre>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-container">
        <div class="page-size">
          <label for="pageSize">Rows per page:</label>
          <select id="pageSize" v-model="pagination.limit" @change="onPageSizeChange">
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="75">75</option>
          </select>
        </div>
        
        <div class="pagination-controls">
          <button 
            class="pagination-btn" 
            :disabled="pagination.page <= 1" 
            @click="goToPage(pagination.page - 1)"
          >
            Previous
          </button>
          
          <span class="pagination-info">
            Page {{ pagination.page }} of {{ pagination.totalPages || 1 }}
          </span>
          
          <button 
            class="pagination-btn" 
            :disabled="pagination.page >= pagination.totalPages" 
            @click="goToPage(pagination.page + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, onUnmounted } from 'vue';
import { format } from 'date-fns';
import axios from 'axios';
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';
import config from '../../config.js';
// Import our custom CSS file
import '@/assets/liven-engage-logs.css';

// State
const logs = ref([]);
const isLoading = ref(false);
const error = ref('');
const expandedRows = ref({});
const currentEnv = ref(config.env);

// Pagination state
const pagination = reactive({
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0
});

// Filters state
const filters = reactive({
  startDatetime: '',
  endDatetime: '',
  searchTerm: ''
});

// Format date to local timezone
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss');
};

// Toggle row details visibility
const toggleDetails = (index) => {
  expandedRows.value = {
    ...expandedRows.value,
    [index]: !expandedRows.value[index]
  };
};

// API endpoint from config
const API_URL = `${config.serverUrl}/api/engage-logs`;

// Fetch logs data with pagination and filters
const fetchLogs = async () => {
  isLoading.value = true;
  error.value = '';
  
  try {
    // Build query parameters
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      sort_by: 'insert_on',
      order: 'desc',
      environment: currentEnv.value
    };
    console.log('Current environment:', currentEnv.value);
    console.log('API URL:', API_URL);
    console.log('API request parameters:', params);
    
    // Add filters if they exist
    if (filters.startDatetime) params.start_datetime = filters.startDatetime;
    if (filters.endDatetime) params.end_datetime = filters.endDatetime;
    if (filters.searchTerm) params.search_term = filters.searchTerm;
    
    // Set timeout to abort request if it takes too long
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    
    const response = await axios.get(API_URL, { 
      params,
      signal: controller.signal,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'X-Requested-With': 'XMLHttpRequest' // Helps some servers optimize response
      }
    });
    
    clearTimeout(timeoutId);
    
    if (response.data.success) {
      logs.value = response.data.logs;
      pagination.total = response.data.pagination.total;
      pagination.totalPages = response.data.pagination.totalPages;
    } else {
      throw new Error(response.data.message || 'Failed to fetch logs');
    }
  } catch (err) {
    // Only show error if not aborted
    if (err.name !== 'AbortError') {
      console.error('Error fetching logs:', err);
      error.value = err.message || 'An error occurred while fetching logs';
    }
  } finally {
    isLoading.value = false;
  }
};

// Refresh logs (same as fetchLogs for simplicity)
const refreshLogs = fetchLogs;

// Handle filter changes
const onFiltersChange = () => {
  pagination.page = 1; // Reset to page 1 when filters change
  fetchLogs();
};

// Handle page size change
const onPageSizeChange = () => {
  pagination.page = 1; // Reset to page 1 when page size changes
  fetchLogs();
};

// Go to specific page
const goToPage = (page) => {
  pagination.page = page;
  fetchLogs();
};

// Helper function to parse JSON data
const parseJsonData = (jsonString) => {
  try {
    if (typeof jsonString === 'object') return jsonString;
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return { error: 'Invalid JSON data' };
  }
};

// Helper function to extract message from log
const getLogMessage = (log) => {
  if (!log.message && log.details) {
    // Try to extract message from details if available
    return log.details.split('\n\nAdditional Info:')[0];
  }
  return log.message || '-';
};

// Load data when component is mounted
onMounted(() => {
  document.title = "Liven Engage Logs";
  fetchLogs();
});

onUnmounted(() => {
  document.title = "Abacus Retail Dashboard";
});
</script>

<style>
/* All styles moved to the external liven-engage-logs.css file */

/* Add these styles to handle message display */
.message-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.full-message {
  margin-bottom: 1rem;
}

.full-message pre {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f5f5f5;
  padding: 0.5rem;
  border-radius: 4px;
  max-height: 500px;
  overflow-y: auto;
}
</style> 